import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PrivacyPolicy from "components/policy/PrivacyPolicy";
import styles from "components/Roku/Welcome/TvPolicyModal.module.scss";

/**
 * Privacy policy shown in a closable modal on the TV welcome screen.
 *
 * TV stores require the policy to be reachable from the app's first screen
 * (Samsung's App Development Privacy Guide, point 1: viewable from the main
 * screen, with the full text available). Navigating to /privacy-policy would
 * take the app away from the welcome screen with no obvious way back on a
 * remote, so the text is shown here instead and the app never leaves the page.
 *
 * Remote-friendly behaviour:
 *   - Close button takes focus on open, so Enter closes it immediately.
 *   - Up/Down (and Page Up/Down) scroll the text, since a remote has no wheel.
 *   - Escape closes it, for mouse/keyboard use in dev.
 *   - Samsung's Return key is owned by TizenRemote, which would otherwise open
 *     the "Exit Game City?" prompt. We announce open/close on the window so it
 *     can defer, and it asks us to close via `gc-overlay-back`.
 *   - Links inside the policy text are inert (see the scss) — on a TV there is
 *     no way back from an outbound navigation.
 */
export default function TvPolicyModal({ onClose }) {
    const scrollRef = useRef(null);
    const closeRef = useRef(null);

    useEffect(() => {
        // Tell TizenRemote (and anything else owning remote keys) to stand down.
        window.dispatchEvent(new CustomEvent("gc-overlay-open"));
        try { closeRef.current?.focus(); } catch (e) { /* focus unavailable */ }

        const onBackRequest = () => onClose();

        const onKeyDown = (e) => {
            const el = scrollRef.current;
            switch (e.key) {
                case "Escape":
                    onClose();
                    break;
                case "ArrowDown":
                    if (el) el.scrollTop += 80;
                    break;
                case "ArrowUp":
                    if (el) el.scrollTop -= 80;
                    break;
                case "PageDown":
                    if (el) el.scrollTop += el.clientHeight * 0.9;
                    break;
                case "PageUp":
                    if (el) el.scrollTop -= el.clientHeight * 0.9;
                    break;
                default:
                    return;
            }
            e.preventDefault();
            e.stopPropagation();
        };

        window.addEventListener("gc-overlay-back", onBackRequest);
        // Capture phase so the game grid / remote handlers don't act on these too.
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            window.dispatchEvent(new CustomEvent("gc-overlay-close"));
            window.removeEventListener("gc-overlay-back", onBackRequest);
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [onClose]);

    // Rendered through a portal to <body>. The welcome card is `transform`ed and
    // `overflow: hidden`, and a transformed ancestor becomes the containing block
    // for position:fixed children — so rendered in place the modal would be
    // trapped inside (and clipped by) the little login card.
    return ReactDOM.createPortal(
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Privacy Policy">
            <div className={styles.panel}>
                <div className={styles.header}>
                    <span className={styles.heading}>Privacy Policy</span>
                    <button ref={closeRef} type="button" className={styles.closeBtn} onClick={onClose}>
                        Close
                    </button>
                </div>

                <div className={styles.body} ref={scrollRef}>
                    <PrivacyPolicy embedded />
                </div>

                <div className={styles.footer}>
                    Use <strong>▲ ▼</strong> to scroll · Select <strong>Close</strong> or press
                    <strong> Return</strong> to go back · Full text also at game.city/privacy-policy
                </div>
            </div>
        </div>,
        document.body
    );
}
